import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useRunOcrScanApi from 'src/services/useRunOcrScanApi';
import { allowedOcrFileExtensions } from 'src/utils/fileValidation';

const RunOcrScan = () => {
  const { confirm } = useConfirm();
  const {
    selectedDocuments,
    refreshPage,
    setRefreshCompany,
    getOcrRecordCount,
    attachmentListData,
  } = useInvoiceScanManager();
  const {
    company: { companyId },
  } = useCompany();
  const { ocrScanRun, isOcrScanRunning } = useRunOcrScanApi();

  const handleRunOcr = useCallback(async () => {
    const runScan = await confirm({
      title: 'Scan Record(s)',
      content: (
        <div className="mb-2 [&>p]:text-sm [&>p]:mb-2 w-[450px]">
          <p>
            The selected record(s) will be scanned and a record will be created based on the image.
            You will find the successfully scanned records listed here in Records tab.
          </p>
          <p>
            Any issues when creating the record will be flagged as "Needs Review" or "Failed" in the
            OCR Queue tab.
          </p>
        </div>
      ),
      confirmButtonText: 'Run Scan',
    });
    if (runScan) {
      const selectedIds = selectedDocuments.map((s) => s.attachId);
      await ocrScanRun({
        companyId: Number(companyId),
        attachments: selectedIds.map((id) => ({ attachmentId: id })),
      });
      toast.success('Run OCR scan Successfully!');
      getOcrRecordCount();
      refreshPage();
      setRefreshCompany?.((prev) => !prev);
    }
  }, [
    confirm,
    selectedDocuments,
    ocrScanRun,
    companyId,
    refreshPage,
    setRefreshCompany,
    getOcrRecordCount,
  ]);

  const isIncludeAttached = selectedDocuments.some((d) => d.receiptId);
  const isIncludeDeleted = selectedDocuments.some((d) => d.isDeleted);

  const isIncludeUnAvailable = selectedDocuments.some((d) => d.signedUrl === null);
  const isIncludesUnsupportedFile = selectedDocuments.some(
    (d) => !allowedOcrFileExtensions.some((ext) => d.originalFile.toLowerCase().endsWith(ext))
  );

  const { informationMessageList, errorMessageList, isRunOcrDisabled } = useMemo(() => {
    const isRunOcrDisabled =
      selectedDocuments.length === 0 ||
      isIncludeAttached ||
      isIncludeDeleted ||
      isIncludeUnAvailable ||
      isIncludesUnsupportedFile;
    const informationMessageList: string[] = [];
    const errorMessageList: string[] = [];
    if (attachmentListData.length === 0) {
      informationMessageList.push('You have no record(s) in queue to Scan.');
    }
    if (selectedDocuments.length === 0) {
      informationMessageList.push('Please select record(s) to Scan.');
    }
    if (isIncludesUnsupportedFile) {
      informationMessageList.push(
        'OCR supports only .PDF, .JPG, .JPEG and .PNG formats. If format is supported, check if file extension was renamed.'
      );
    }
    if (isIncludeAttached) {
      errorMessageList.push('Please select only Unattached record(s) to Scan.');
    }
    if (isIncludeDeleted) {
      errorMessageList.push('Please select only Undeleted record(s) to Scan.');
    }
    if (isIncludeUnAvailable) {
      errorMessageList.push('Please select record(s) with available attachment file.');
    }

    return {
      isRunOcrDisabled,
      informationMessageList,
      errorMessageList,
    };
  }, [
    attachmentListData.length,
    isIncludeAttached,
    isIncludeDeleted,
    isIncludeUnAvailable,
    isIncludesUnsupportedFile,
    selectedDocuments.length,
  ]);

  return (
    <PopUp
      content={
        <div className="m-1 max-w-[300px]">
          <PopUpContentWrapper type="Error" contents={errorMessageList} />
          <PopUpContentWrapper type="Information" contents={informationMessageList} />
        </div>
      }
      show={isRunOcrDisabled}
    >
      <Button
        color="info"
        disabled={isRunOcrDisabled}
        onClick={handleRunOcr}
        loading={isOcrScanRunning}
      >
        Run OCR Scan
      </Button>
    </PopUp>
  );
};

export default RunOcrScan;
